import React from 'react';
import { Box, Grid, Typography, Checkbox, Avatar } from '@mui/material';
import TeamMemberCard from './TeamMemberCard';
import avatarPlaceholder from 'images/avatar-cropped.png';
import TeamMemberTemplateButton from './TeamMemberTemplateButton';
import TeamMemberSubteamButton from './TeamMemberSubteamButton';
import TeamMemberListItem from './TeamMemberListItem';

const TeamMembersCardContainer = ({
    onCreateCardClick,
    onEditCardClick,
    searchQuery,
    items = [],
    selectedCards = new Set(),
    onCardSelection = () => { },
    teamId,
    viewMode = 'grid'
}) => {
    // Filter items based on search query
    const filteredMembers = items.filter(member => {
        if (!searchQuery) return true;

        const searchLower = searchQuery.toLowerCase();
        // Check data from defaultCard first, then user, then member level
        const fullName = (
            member.defaultCard?.contact_info?.fullName || 
            member.defaultCard?.cardName || 
            member.user?.fullName || 
            member.contact_info?.fullName || 
            member.cardName || 
            ''
        ).toLowerCase();
        const email = (
            member.defaultCard?.contact_info?.email || 
            member.user?.email || 
            member.email || 
            ''
        ).toLowerCase();
        const jobTitle = (
            member.defaultCard?.contact_info?.jobTitle || 
            member.contact_info?.jobTitle || 
            ''
        ).toLowerCase();
        const company = (
            member.defaultCard?.contact_info?.company || 
            member.contact_info?.company || 
            ''
        ).toLowerCase();

        return fullName.includes(searchLower) ||
            email.includes(searchLower) ||
            jobTitle.includes(searchLower) ||
            company.includes(searchLower);
    });

    // Show empty state
    if (filteredMembers.length === 0) {
        return (
            <Box sx={{ padding: '1rem', textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'No members found matching your search' : 'No members found.'}
                </Typography>
            </Box>
        );
    }

    const getMemberName = (member) => {
        return (
            member.defaultCard?.contact_info?.fullName ||
            member.defaultCard?.cardName ||
            member.user?.fullName ||
            member.contact_info?.fullName ||
            member.cardName ||
            member.fullName ||
            'Unknown User'
        );
    };

    const getJobTitle = (member) => {
        return (
            member.defaultCard?.contact_info?.jobTitle ||
            member.contact_info?.jobTitle ||
            ''
        );
    };

    const getEmail = (member) => {
        if (member?.user?.isCompanyPage) {
            return 'Company Page';
        }
        return (
            member.defaultCard?.contact_info?.email ||
            member.user?.email ||
            member.email ||
            ''
        );
    };

    const secondaryTextColor = '#666';

    const getMemberRoleLabel = (member) => {
        const role = member?.role || member?.user?.role;
        const isAdmin =
            role === 'admin' ||
            role === 'team_admin' ||
            role === 'owner' ||
            role === 'teamOwner' ||
            member?.isTeamAdmin;
        return isAdmin ? 'TEAM ADMIN' : '';
    };

    const handleRowClick = (event, member) => {
        if (event.target.closest('.MuiCheckbox-root')) return;
        if (event.target.closest('[data-member-action]')) return;
        onEditCardClick(member);
    };

    if (viewMode === 'list') {
        const totalSelectable = filteredMembers.length;
        const selectedCount = filteredMembers.filter(member => selectedCards.has(member._id)).length;
        const isAllSelected = totalSelectable > 0 && selectedCount === totalSelectable;
        const isSomeSelected = selectedCount > 0 && selectedCount < totalSelectable;

        const handleSelectAll = (event) => {
            const shouldSelect = event.target.checked;
            filteredMembers.forEach((member) => {
                const isSelected = selectedCards.has(member._id);
                if (shouldSelect && !isSelected) {
                    onCardSelection(member._id, true);
                }
                if (!shouldSelect && isSelected) {
                    onCardSelection(member._id, false);
                }
            });
        };

        return (
            <Box sx={{ padding: '1rem', width: '100%', boxSizing: 'border-box' }}>
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        borderRadius: '0.75rem',
                        overflow: 'hidden'
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0.75rem 1rem',
                            color: '#666',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            borderBottom: '1px solid #f0f0f0',
                            backgroundColor: '#fff'
                        }}
                    >
                        <Box
                            sx={{
                                width: '3rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                            }}
                        >
                            <Checkbox
                                checked={isAllSelected}
                                indeterminate={isSomeSelected}
                                onChange={handleSelectAll}
                                sx={{
                                    color: '#d0d0d0',
                                    padding: 0,
                                    '&.Mui-checked': {
                                        color: '#a0a0a0'
                                    },
                                    '&.MuiCheckbox-indeterminate': {
                                        color: '#a0a0a0'
                                    },
                                    '& .MuiSvgIcon-root': {
                                        borderRadius: '0.5rem',
                                        backgroundColor: '#fff'
                                    }
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                position: 'absolute',
                                left: '3.25rem',
                                    fontWeight: 600,
                                    color: '#666',
                                    whiteSpace: 'nowrap',
                                    pointerEvents: 'none',
                                    backgroundColor: '#fff'
                                }}
                            >
                                Select All
                            </Typography>
                        </Box>
                        <Box sx={{ width: '4.5rem' }} />
                        <Box sx={{ flex: '1 1 12rem' }} />
                        <Box sx={{ flex: '1 1 10rem' }} />
                        <Box sx={{ flex: '0 0 12rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            Template
                        </Box>
                        <Box sx={{ flex: '0 0 12rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            Subteam
                        </Box>
                        <Box sx={{ width: '4rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            Actions
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {filteredMembers.map((member, index) => (
                            <TeamMemberListItem
                                key={member._id}
                                member={member}
                                isSelected={selectedCards.has(member._id)}
                                onSelectionChange={(isSelected) => onCardSelection(member._id, isSelected)}
                                onEditCardClick={onEditCardClick}
                                teamId={teamId}
                                index={index}
                                isLast={index === filteredMembers.length - 1}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: '1rem', width: '100%', boxSizing: 'border-box' }}>
            <Grid container spacing={2}>
                {filteredMembers.map((member) => (
                    <TeamMemberCard
                        key={member._id}
                        member={member}
                        onEditCardClick={onEditCardClick}
                        isSelected={selectedCards.has(member._id)}
                        onSelectionChange={(isSelected) => onCardSelection(member._id, isSelected)}
                        teamId={teamId}
                    />
                ))}
            </Grid>
        </Box>
    );
};

export default TeamMembersCardContainer;